import { Button, DrawerBody, DrawerHeader } from '@chakra-ui/react';
import { format } from 'date-fns-tz';
import { Form, Formik } from 'formik';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DrawerFixedButton } from 'components/Drawer';
import Tabs, { TabPanels, TabHeader } from 'components/Tabs';
import { MONTH_DAY_YEAR_DASH_FORMAT } from 'global/appConstants';
import { useSaveCategory } from 'hooks/timeOff/useSaveCategory';

import Accrual from './Accrual';
import General from './General';
import { initialCategoryFormData, categoryValidationSchema } from './schema';

interface IAddCategory {
  onClose: () => void;
  formData?: typeof initialCategoryFormData;
}

const CategoryForm: React.FC<IAddCategory> = ({ onClose, formData = initialCategoryFormData }) => {
  const { t } = useTranslation(['timeOff', 'common']);

  const { status: saveCategoryStatus, mutateAsync: mutateAsyncCategory } = useSaveCategory();

  const tabHeaders = useMemo(
    () => [
      { label: 'General', tabName: 'General' },
      { label: 'Accrual', tabName: 'Accrual' },
    ],
    []
  );
  const tabPanels = useMemo(() => [{ panel: <General /> }, { panel: <Accrual /> }], []);

  const handleFormSubmit = async (values: typeof initialCategoryFormData) => {
    const postData = {
      id: formData?.id,
      name: values.name,
      description: values.description,
      active: values.active,
      is_paid: values.is_paid === 'true' ? true : false,
      in_hours: values.in_hours === 'true' ? true : false,
      color: typeof values.color === 'string' ? values.color : values?.color?.hex || '',
      user_ids: values.users.map((user: any) => user.userId),
      accrue_per_period: values.accrue_per_period,
      accrue_type: values.accrue_type?.value,
      accrue_period: values.accrue_type?.value === 'Hourly' ? values.accrue_hourly : values.accrue_period,

      // waiting period
      set_waiting_period: values.set_waiting_period === '1' ? true : false,
      accrue_start:
        values.set_waiting_period === '1' && values.accrue_start
          ? format(values.accrue_start, MONTH_DAY_YEAR_DASH_FORMAT)
          : undefined,

      // Employee Accrual Balance Cap
      set_max_balance: values.set_max_balance === '1' ? true : false,
      max_balance: values.set_max_balance === '1' ? values.max_balance : '',

      // Carryover Expiration
      reset_balance_yearly: values.reset_balance_yearly === '1' ? true : false,
      max_carryover_balance:
        values.carryover_expiration_type?.value === 'up-to' ? Number(values.max_carryover_balance) : null,

      allow_negative_balance: values.allow_negative_balance,

      // Leave Duration
      max_request_duration: Number(values.max_request_duration),
      min_request_duration: Number(values.min_request_duration),

      initial_balance: Number(values.initial_balance),
    };

    mutateAsyncCategory(postData).then(() => {
      if (onClose) {
        onClose();
      }
    });
  };

  return (
    <>
      <DrawerHeader>{formData?.id ? 'Update Time Off Category' : 'Create Time Off Category'}</DrawerHeader>

      <DrawerBody pb="20">
        <Formik
          enableReinitialize
          initialValues={formData}
          validationSchema={categoryValidationSchema}
          onSubmit={handleFormSubmit}
        >
          {() => (
            <Form noValidate autoComplete="off">
              <Tabs>
                <TabHeader tabHeaders={tabHeaders} />
                <TabPanels tabPanels={tabPanels} />{' '}
              </Tabs>

              <DrawerFixedButton>
                <Button
                  colorScheme="secondary"
                  type="submit"
                  width="100%"
                  size="lg"
                  isLoading={saveCategoryStatus === 'loading'}
                >
                  {formData?.id ? t('timeOff:updateCategory') : t('timeOff:addCategory')}
                </Button>
              </DrawerFixedButton>
            </Form>
          )}
        </Formik>
      </DrawerBody>
    </>
  );
};
export default CategoryForm;
